import ReactGA from 'react-ga'
import axios from 'axios'
import { datadogRum } from '@datadog/browser-rum'
import * as JwtDecode from 'jwt-decode'
import { get } from 'lodash'

import Api, { Auth, OpenAPI } from 'services/Api'
import { createErrorNotification } from 'store/actions/notifications'
import { getSocket } from 'store/middleware/realtime'
import { isProd } from 'services/Utilities'

import { Intercom } from '@capacitor-community/intercom'
import { Capacitor } from '@capacitor/core'

let Socket

// De-encode the token, extract the payload and parse the json string
export const decodeToken = (token = localStorage.getItem('token')) => JwtDecode(token)

export const setLoggedOut = async store => {
  // Unregister from push notification
  if (Capacitor.isNativePlatform()) {
    try {
      const {
        notifications: { deviceId, deviceType: type }
      } = store.getStore()

      // Unregister the push device
      if (deviceId && type) await Api.post(`/push/unregister`, { deviceId, type })
    } catch (err) {
      console.error(err)
    }
  }

  localStorage.clear()
  Socket.off('removeFromResources')

  // Remove intercom
  delete window.intercomSettings
  if (window.Intercom) window.Intercom('shutdown')

  store.dispatch({ type: 'SET_LOGGED_OUT' })
}

export const setLoggedIn = (token, renewToken) => {
  return async dispatch => {
    // Save the token
    localStorage.setItem('token', token)
    // If a renew token is passed, save it
    if (renewToken) localStorage.setItem('renewToken', renewToken)

    Socket = getSocket()
    resetTokenHeaders(token)
    const decodedToken = decodeToken(token)
    const isPunchToken = decodedToken.punchToken
    const restrictToPunchTerminal = get(decodedToken, 'restrictToPunchTerminal')
    if (restrictToPunchTerminal) dispatch({ type: 'SET_TRIGGER_PUNCH_OPENING' })

    // Detect if user is blocking sentry by attempting to load their CDN
    let adBlocker = false
    try {
      if (isProd) await axios.get('https://browser.sentry-cdn.com/6.1.0/bundle.min.js')
    } catch (err) {
      adBlocker = true
    }

    if (isProd) {
      // Google Analytics info
      ReactGA.set({ userId: decodedToken.uid, dimension1: decodedToken.co })

      datadogRum.setUser({
        id: decodedToken.uid,
        name: decodedToken.rn,
        email: decodedToken.email,
        company: decodedToken.co,
        username: decodedToken.username,
        type: decodedToken.type,
        punchTerminal: isPunchToken
      })
    }

    // Disables Intercom for the punch terminal
    if (!isPunchToken) {
      // Intercom
      window.intercomSettings = {
        app_id: 'mu2i18qo',
        name: decodedToken.rn, // Full name
        user_id: decodedToken.uid,
        user_hash: decodedToken.huid,
        language_override: decodedToken.lang,
        user_language_in_merinio: decodedToken.lang,
        Company: decodedToken.co,
        version: process.env.REACT_APP_CURRENT_VERSION,
        userType: decodedToken.type,
        adBlocker,
        custom_launcher_selector: '#intercom_launcher'
      }
      if (decodedToken.dev) window.intercomSettings.app_id = 'r8tz2p2m'
      if (isPunchToken) window.intercomSettings.punchTerminal = isPunchToken
      dispatch({ type: 'SET_ACCESS_INTERCOM', showIntercom: true })
      if (window.Intercom) window.Intercom('boot', window.intercomSettings)
      document.addEventListener('deviceready', () => registerIntercomForPush(decodedToken), false)
    }

    dispatch({ type: 'SET_LOGGED_IN', token, decodedToken })
    dispatch({ type: 'SET_COMPANY_NAME', co: decodedToken.co })
  }
}

export const renewToken = async store => {
  // Fetch the token into the store
  const { decodedToken } = store.getState().auth
  // If no token is present, logout the user
  if (!decodeToken) return setLoggedOut(store)

  const getNewToken = async () => {
    // Query the API to fetch a renewed token
    const { success, token } = await Auth.post('', {
      credentials: { co: decodedToken.co, type: 'Renew', username: decodedToken.user, password: localStorage.getItem('renewToken') }
    })
    // If not a success (renew token is expired), logout the user
    if (!success) {
      store.dispatch(createErrorNotification())
      return setLoggedOut(store)
    }

    // If we got a token, relogin the user with the new token
    store.dispatch(setLoggedIn(token))

    // Modify the API request
    resetTokenHeaders(token)
    if (!decodeToken(token).groups.length) setLoggedOut(store)

    return token
  }

  const newTokenPromise = getNewToken()

  store.dispatch({ type: 'SET_IS_RENEWING_TOKEN', newTokenPromise })

  return await newTokenPromise
}

// Reset the token info in API and in the file variable
export const resetTokenHeaders = token => {
  Api.defaults.headers.common['token'] = token
}

export const setCompany = co => {
  return dispatch => dispatch({ type: 'SET_COMPANY_NAME', co })
}

export const lookupCompanyLang = () => {
  return async (dispatch, getStore) => {
    const {
      auth: { co }
    } = getStore()

    // Lookup company name and default language
    const { success, lang } = await OpenAPI.post('/lookup', { company: co })
    if (success) return dispatch({ type: 'SET_COMPANY_LANG', lang: lang })
    // If the name is invalid, set it as null
    if (!success) return dispatch({ type: 'SET_COMPANY_NAME', co: null })
  }
}

async function registerIntercomForPush(decodedToken) {
  await Intercom.registerIdentifiedUser({
    userId: decodedToken.uid,
    email: decodedToken.email
  })

  await Intercom.updateUser({
    userId: decodeToken.uid,
    languageOverride: decodedToken.lang,
    name: decodedToken.rn,
    customAttributes: {
      id: decodeToken.uid,
      Company: decodedToken.co,
      version: process.env.REACT_APP_CURRENT_VERSION,
      userType: decodedToken.type
    }
  })
}
